// React
import { memo, useCallback, useMemo } from "react";

// Servisni sloj
import { PricingCombinationFormValues } from "services/api/pricing/types";

// UI
import { Button } from "ui/components/Button/Button";
import { MultiDropdownOption } from "ui/components/Dropdown/MultiDropdown/types";

// Lokalne komponente
import PricingStepIntro from "ui/modules/pricing/components/PricingStepIntro/PricingStepIntro";
import PricingCombinationRow from "./components/PricingCombinationRow/PricingCombinationRow";

// Tipovi
import { PricingCombinationsStepProps } from "./types";

/**
 * Cijena po vozilu, po kombinaciji packova.
 *
 * Ključ kombinacije se ne unosi — backend ga izvodi iz šifara packova, pa ista kombinacija
 * uvijek dobije isti ključ.
 */
const PricingCombinationsStep: React.FC<PricingCombinationsStepProps> = (
  props: PricingCombinationsStepProps
) => {
  const { formValues, setFormValues, packs, errors } = props;

  const packOptions: MultiDropdownOption[] = useMemo(
    () => packs.map((pack) => ({ value: pack.code, text: pack.name })),
    [packs]
  );

  const handlePriceChange = useCallback(
    (value: number | null, name?: string) => {
      if (!name) return;

      const index = Number(name.replace("combination-price-", ""));

      setFormValues((previous) => ({
        ...previous,
        combinations: previous.combinations.map((combination, i) =>
          i === index ? { ...combination, price: value } : combination
        ),
      }));
    },
    [setFormValues]
  );

  const handlePacksChange = useCallback(
    (name: string, items: MultiDropdownOption[]) => {
      const index = Number(name.replace("combination-packs-", ""));

      setFormValues((previous) => ({
        ...previous,
        combinations: previous.combinations.map((combination, i) =>
          i === index ? { ...combination, packCodes: items.map((item) => item.value) } : combination
        ),
      }));
    },
    [setFormValues]
  );

  const handleDescriptionChange = useCallback(
    (e: any) => {
      const index = Number((e?.target?.name ?? "").replace("combination-description-", ""));
      const value = e?.target?.value ?? null;

      setFormValues((previous) => ({
        ...previous,
        combinations: previous.combinations.map((combination, i) =>
          i === index ? { ...combination, description: value } : combination
        ),
      }));
    },
    [setFormValues]
  );

  const handleAddCombination = useCallback(() => {
    setFormValues((previous) => {
      const next: PricingCombinationFormValues = {
        packCodes: [],
        price: null,
        description: null,
        sortOrder: previous.combinations.length + 1,
      };

      return { ...previous, combinations: [...previous.combinations, next] };
    });
  }, [setFormValues]);

  const handleRemoveCombination = useCallback(
    (index: number) => {
      setFormValues((previous) => ({
        ...previous,
        combinations: previous.combinations.filter((_, i) => i !== index),
      }));
    },
    [setFormValues]
  );

  return (
    <>
      <PricingStepIntro
        title="Cijena po vozilu"
        description={
          <>
            Odredite mjesečnu cijenu po vozilu za svaku kombinaciju paketa. Dodavanjem novog
            packa matrica se širi ovdje, <strong>bez izmjene koda</strong>.
          </>
        }
      />

      <div className="p-4 w-full max-w-4xl mx-auto">
        <div className="flex flex-col gap-4">
          {formValues.combinations.map((combination, index) => (
            <PricingCombinationRow
              key={index}
              index={index}
              combination={combination}
              packOptions={packOptions}
              errors={errors}
              onPacksChange={handlePacksChange}
              onDescriptionChange={handleDescriptionChange}
              onPriceChange={handlePriceChange}
              onRemove={handleRemoveCombination}
            />
          ))}
        </div>

        {/* <div className="mt-4">
          <Button variant="outlined" children="Dodaj kombinaciju" onClick={handleAddCombination} />
        </div> */}
      </div>
    </>
  );
};

export default memo(PricingCombinationsStep);
